import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import { Button } from '../../../../../../ui';
import { JobPostingTabData } from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';

interface VideoData {
  title: string;
  description: string;
  videoUrl: string | null;
}

interface VideoMessageDetailsProps {
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
  jobPostingData: JobPostingTabData;
  setJobPostingData: React.Dispatch<React.SetStateAction<JobPostingTabData>>;
}

const VideoMessageDetails = ({
  setActiveTab,
  jobPostingData,
  setJobPostingData,
}: VideoMessageDetailsProps) => {
  const { t } = useTranslation(NS.adminPages);
  const [videoData, setVideoData] = useState<VideoData>({
    title: '',
    description: '',
    videoUrl: null,
  });
  const [isRecording, setIsRecording] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunks: Blob[] = [];
  const [micList, setMicList] = useState<SelectOptions[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string | null>(null);

  useEffect(() => {
    const getMicrophones = async () => {
      if (jobPostingData.enableVideo) {
        try {
          // Ask for permission to access audio only when video is enabled
          await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: true,
          });

          const devices = await navigator.mediaDevices.enumerateDevices();
          const mics = devices
            .filter((device) => device.kind === 'audioinput')
            .map((device) => ({
              value: device.deviceId,
              label: device.label || 'Microphone',
            }));
          setMicList(mics);
          if (mics.length > 0) {
            setSelectedMicId(mics[0].value);
          }
        } catch (err) {
          toast.error('Error accessing microphone devices:', {
            position: 'bottom-center',
          });
        }
      } else {
        // Clear the mic list when video is disabled
        setMicList([]);
        setSelectedMicId(null);
      }
    };

    getMicrophones();
  }, [jobPostingData.enableVideo]);

  const startRecording = async () => {
    if (!jobPostingData.enableVideo) {
      toast.error('Please enable video posting first', {
        position: 'bottom-center',
      });
      return;
    }

    if (selectedMicId) {
      try {
        const constraints = {
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
          audio: {
            deviceId: selectedMicId,
          },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // Mute the audio for the live playback video, but don't disable the track for recording
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true; // Mute the playback video
          await videoRef.current.play();
        }

        mediaRecorderRef.current = new MediaRecorder(stream);
        mediaRecorderRef.current.ondataavailable = (event) => {
          recordedChunks.push(event.data);
        };
        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(recordedChunks, { type: 'video/webm' });
          const url = URL.createObjectURL(blob);

          setVideoData((prev) => ({ ...prev, videoUrl: url }));
          setJobPostingData({
            ...jobPostingData,
            videoFilename: new File([blob], 'recorded-video.mp4', {
              type: 'video/webm',
            }),
          });

          stream.getTracks().forEach((track) => track.stop());

          if (videoRef.current) {
            videoRef.current.srcObject = null;
            videoRef.current.src = url;
            videoRef.current.play();
          }
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);
      } catch (error) {
        toast.error('Error accessing media devices.', {
          position: 'bottom-center',
        });
      }
    } else {
      toast.error('Please select a Mic option before you record', {
        position: 'bottom-center',
      });
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      // Stop all media tracks immediately
      const stream = videoRef.current?.srcObject as MediaStream;
      stream?.getTracks().forEach((track) => track.stop());

      // Stop the recorder
      mediaRecorderRef.current.stop();

      // Clear the video source to remove live feed immediately
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setIsRecording(false);
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!jobPostingData.enableVideo) {
      toast.error('Please enable video posting first', {
        position: 'bottom-center',
      });
      return;
    }

    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setJobPostingData({ ...jobPostingData, videoFilename: file });
      setVideoData((prev) => ({ ...prev, videoUrl: url }));

      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = url;
        videoRef.current.play();
      }
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setVideoData((prev) => ({ ...prev, [name]: value }));
    if (name === 'description') {
      setJobPostingData({ ...jobPostingData, videoDescription: value });
    } else if (name === 'title') {
      setJobPostingData({ ...jobPostingData, videoTitle: value });
    }
  };

  return (
    <div className=' flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100'>
      <div className='p-6  w-full bg-white rounded-xl shadow-lg backdrop-blur-sm bg-opacity-95 transition-all duration-300'>
        <div className='flex items-center justify-between mb-4 pb-2 border-b border-gray-100'>
          <h1 className='text-2xl font-light text-gray-800 tracking-wide'>
            { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.heading) }
          </h1>
          <div className='flex items-center gap-3'>
            <span className='text-sm font-medium text-gray-700'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.useVideo) }
            </span>
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                  type='checkbox'
                  className='sr-only peer'
                  checked={jobPostingData.enableVideo}
                  onChange={(e) => {
                    setJobPostingData({
                      ...jobPostingData,
                      enableVideo: e.target.checked,
                      ...(e.target.checked
                        ? {}
                        : {
                          videoFilename: undefined,
                          videoTitle: '',
                          videoDescription: '',
                        }),
                    });
                    if (!e.target.checked) {
                      setVideoData({
                        title: '',
                        description: '',
                        videoUrl: null,
                      });
                      if (videoRef.current) {
                        videoRef.current.src = '';
                      }
                    }
                  }}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
            </label>
            { jobPostingData.enableVideo && jobPostingData.videoFilename && (
              <span className='text-sm text-green-600 font-medium ml-2'>
                { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.videoReady) }
              </span>
            ) }
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 w-full'>
          <div className='flex flex-col gap-4'>
            <div className='group'>
              <label className='block text-sm font-medium text-gray-500 mb-1 transition-colors group-hover:text-gray-700'>
                { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.selectMicrophone) }
              </label>
              <select
                  className={`w-full px-3 py-2 bg-gray-50 border-0 rounded-lg focus:ring-2 focus:ring-indigo-200 transition-all duration-200 ${
                    !jobPostingData.enableVideo
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                  onChange={(e) => setSelectedMicId(e.target.value)}
                  value={selectedMicId || ''}
                  disabled={!jobPostingData.enableVideo}
              >
                <option value=''>{ t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.chooseAudioDevice) }</option>
                { micList.map((mic) => (
                  <option key={mic.value} value={mic.value}>
                    { mic.label }
                  </option>
                )) }
              </select>
            </div>

            <div className='group'>
              <label className='block text-sm font-medium text-gray-500 mb-2 transition-colors group-hover:text-gray-700'>
                { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.titleLabel) }
              </label>
              <input
                  type='text'
                  name='title'
                  value={jobPostingData.videoTitle || ''}
                  onChange={handleInputChange}
                  placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.titlePlaceholder)}
                  disabled={!jobPostingData.enableVideo}
                  className={`w-full px-3 py-2 bg-gray-50 border-0 rounded-lg focus:ring-2 focus:ring-indigo-200 transition-all duration-200 ${
                    !jobPostingData.enableVideo
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
              />
            </div>
            <div className='group'>
              <label className='block text-sm font-medium text-gray-500 mb-1 transition-colors group-hover:text-gray-700'>
                { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.description) }
              </label>
              <textarea
                  name='description'
                  value={jobPostingData.videoDescription || ''}
                  onChange={handleInputChange}
                  placeholder={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.descriptionPlaceholder)}
                  disabled={!jobPostingData.enableVideo}
                  className={`w-full px-3 py-2 bg-gray-50 border-0 rounded-lg focus:ring-2 focus:ring-indigo-200 transition-all duration-200 resize-none ${
                    !jobPostingData.enableVideo
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                  rows={3}
              />
            </div>
            { /* Add a note to enable use video in Post to enable the fields and it will ask browser for permission */ }
            <p className='text-xs text-orange-500'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.note) }
            </p>
          </div>

          <div
              className={`rounded-lg overflow-hidden bg-gray-50 p-4 transition-all duration-300 ${
                !jobPostingData.enableVideo
                  ? 'opacity-50 pointer-events-none'
                  : ''
              }`}
          >
            <div className='rounded-lg overflow-hidden border border-gray-100 shadow-sm mb-3 bg-black'>
              <video
                  ref={videoRef}
                  controls={!isRecording && jobPostingData.enableVideo}
                  width={640}
                  height={480}
                  className='w-full'
              >
                { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.browserNotSupported) }
              </video>
            </div>

            <div className='flex items-center gap-3'>
              <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={!jobPostingData.enableVideo}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                    isRecording
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                  } focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    isRecording ? 'focus:ring-red-400' : 'focus:ring-indigo-400'
                  } ${
                    !jobPostingData.enableVideo
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
              >
                { isRecording ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.stopRecording) : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.startRecording) }
              </button>

              <label
                  htmlFor='file-upload'
                  className={`flex-1 cursor-pointer px-4 py-2 text-sm font-medium text-center bg-gray-800 hover:bg-gray-900 text-white rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-700 ${
                    !jobPostingData.enableVideo
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
              >
                { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.uploadVideo) }
                <input
                    id='file-upload'
                    type='file'
                    accept='video/*'
                    onChange={handleUpload}
                    className='hidden'
                    disabled={!jobPostingData.enableVideo}
                />
              </label>
            </div>

            <p className='mt-2 text-xs text-gray-500 text-center'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.supportedFormats) }
            </p>
          </div>
        </div>
        <div className='flex justify-end'>
          <Button
              onClick={() => setActiveTab('preQualifyingQuestions')}
              variant={'primary'}
              label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.video.continue)}
          />
        </div>
      </div>
    </div>
  );
};
export default VideoMessageDetails;
